import { useEffect } from 'react'
import { AppIcon, nearestAppIconSize } from './ui/icon'
import { mdiAccount } from '@mdi/js'
import { useEntityPicture } from '../hooks/useEntityPicture'
import { cn } from '../lib/utils'

export interface UserAvatarProps {
  org: string
  userUid: string
  /** Whether the user has a picture set (derived from `user.picture`). */
  hasPicture: boolean
  /** Rendered size in pixels (width & height). Defaults to 24. */
  size?: number
  /** User name, used as the `<img>` alt text. */
  name?: string | null
  className?: string
}

/**
 * Small, read-only circular avatar for list rows. Unlike `EntityPicture`, it has
 * no upload / drag-drop / modal logic — it just shows the user's picture (ETag-cached
 * via `useEntityPicture`) or a muted placeholder circle so rows stay aligned.
 */
export function UserAvatar({
  org,
  userUid,
  hasPicture,
  size = 24,
  name,
  className,
}: UserAvatarProps) {
  const { state, loadPicture } = useEntityPicture('user', userUid, org, {
    enabled: hasPicture,
  })

  useEffect(() => {
    if (hasPicture) {
      loadPicture()
    }
  }, [hasPicture, loadPicture])

  const dimensions = { width: size, height: size }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground',
        className,
      )}
      style={dimensions}
      data-testid={`user-avatar-${userUid}`}
    >
      {hasPicture && state.imageUrl ? (
        <img
          src={state.imageUrl}
          alt={name || ''}
          className="h-full w-full rounded-full object-cover"
        />
      ) : (
        <AppIcon path={mdiAccount} size={nearestAppIconSize(size * 0.75)} />
      )}
    </span>
  )
}
